import React from 'react';

import { useSelector } from 'react-redux';

import { UnitType } from '@src/core/enums';
import { arcConvertUnit, arcSpatiumUnit } from '@src/core/extensions';
import { MeasurementType } from '@src/core/features/settings/entities';
import { useUnitType } from '@src/core/features/settings';

import { terrainCacheKey } from '@src/features/order/pages/upsert-mission-page/config/cache-keys';
import { CRASH_ZONE_BUFFER } from '@src/features/order/pages/upsert-mission-page/config/terrain';
import {
  selectTerrainMode,
  selectWaypoints,
  useGetTerrainAwareMutation,
} from '@src/features/order/pages/upsert-mission-page/redux';
import { TerrainMode } from '@src/features/order/pages/upsert-mission-page/redux/extra-store/types';
import {
  analyzeIntervals,
  calculateDistance,
  interpolateAltitude,
} from '@src/features/order/pages/upsert-mission-page/utils';

// NOTE: This hook is the single source of truth for terrain-aware series and
// collision checks shared between the chart (TerrainAware) and the alert
// (TerrainAlert). It only READS the RTK Query cache via `fixedCacheKey`; the
// mutation trigger lives in TerrainAware to avoid duplicate POSTs.
const useTerrainSeries = () => {
  const wps = useSelector(selectWaypoints);
  const terrainMode = useSelector(selectTerrainMode);
  const isAmsl = terrainMode === TerrainMode.AMSL;
  const isAgl = terrainMode === TerrainMode.AGL;

  const [, { data = [], isLoading }] = useGetTerrainAwareMutation({
    fixedCacheKey: terrainCacheKey,
  });

  const unitType = useUnitType();
  const buffer = arcConvertUnit(CRASH_ZONE_BUFFER, MeasurementType.DISTANCE, unitType);
  const unit = arcSpatiumUnit(unitType);

  const wpDistances = React.useMemo(() => calculateDistance(wps), [wps]);

  const minTerrainAlt = React.useMemo(() => {
    if (data.length === 0) return 0;
    return Math.min(...data.map((d) => Number(d.altitude)));
  }, [data]);

  // RELATIVE ("Relative to Launch") uses takeoff terrain AMSL as the chart's zero,
  // so terrain below launch renders as negative values.
  const launchTerrainAlt = React.useMemo(() => {
    if (data.length === 0) return 0;
    return Number(data[0]?.altitude ?? 0);
  }, [data]);

  const convertAltitude = React.useCallback(
    (value: number) =>
      arcConvertUnit(
        value,
        MeasurementType.DISTANCE,
        unitType
      ),
    [unitType]
  );

  const droneData = React.useMemo(() => {
    if (data.length === 0) {
      return wpDistances.map((wp) => ({
        ...wp,
        // wp.altitude is already in the user's display unit — do NOT re-convert.
        altitude: Number(wp.altitude),
      }));
    }

    // AMSL + Relative to Launch
    if (!isAgl) {
      return wpDistances.map((wp) => ({
        ...wp,
        // wp.altitude is already in the user's display unit — do NOT re-convert.
        altitude: Number(wp.altitude),
      }));
    }

    // AGL
    if (wpDistances.length < 2) {
      return wpDistances.map((wp) => ({
        ...wp,
        // wp.altitude is already in the user's display unit — do NOT re-convert.
        altitude: Number(wp.altitude),
      }));
    }

    const terrainWithDist = calculateDistance(data);

    return wpDistances.map((wp) => {
      const terrainAtWp = interpolateAltitude(
        wp.distance,
        terrainWithDist
      );

      const relTerrain = terrainAtWp - minTerrainAlt;

      return {
        // relTerrain is SI (convert to display); wp.altitude is already display.
        ...wp,
        altitude: convertAltitude(relTerrain) + Number(wp.altitude),
      };
    });
  }, [
    wpDistances,
    isAgl,
    data,
    minTerrainAlt,
    convertAltitude,
  ]);

  const terrainData = React.useMemo(() => {
    let terrain;

    if (isAmsl) {
      terrain = calculateDistance(data);
    } else if (isAgl) {
      terrain = calculateDistance(data, minTerrainAlt);
    } else {
      terrain = calculateDistance(data, launchTerrainAlt);
    }

    return terrain.map((p) => ({
      ...p,
      altitude: convertAltitude(Number(p.altitude)),
    }));
  }, [
    data,
    isAmsl,
    isAgl,
    minTerrainAlt,
    launchTerrainAlt,
    convertAltitude,
  ]);

  const crashZoneData = React.useMemo(
    () => terrainData.map((p) => ({ ...p, altitude: Number(p.altitude) + buffer })),
    [terrainData, buffer],
  );

  const hasTerrainCollision = React.useMemo(
    () => analyzeIntervals(droneData, terrainData, 0).length > 0,
    [droneData, terrainData],
  );

  const hasCrashZoneCollision = React.useMemo(
    () => analyzeIntervals(droneData, terrainData, buffer).length > 0,
    [droneData, terrainData, buffer],
  );

  return {
    isLoading,
    isAmsl,
    isAgl,
    droneData,
    terrainData,
    crashZoneData,
    wpDistances,
    buffer,
    unit,
    hasTerrainCollision,
    hasCrashZoneCollision,
  };
};

export default useTerrainSeries;
